import {
  ImportValidationError,
  isIsoUtc,
  isUuid,
  nowIso,
  type DocumentRecord,
  type IdFactory,
  type ReadingStateRecord,
} from "@/domain";
import {
  toStorageWriteError,
  type DatabaseInvalidationPublisher,
  type MarginaliaDatabase,
} from "@/storage";

import {
  mergeAnnotations,
  type AnnotationMergeCounts,
} from "./merge-annotations";
import { migrateDocumentExportToCurrent } from "./migrations";
import {
  marginaliaExportV1Schema,
  type MarginaliaExportV1,
} from "./schemas/v1";
import {
  JSON_IMPORT_MAX_BYTES,
  readJsonImportValue,
  type ReadJsonImportOptions,
} from "./read-import";

export { JSON_IMPORT_MAX_BYTES };

const PREVIEW_DOCUMENT_ID = "00000000-0000-4000-8000-000000000000";

export type ParseDocumentImportOptions = ReadJsonImportOptions;

export type DocumentImportPreview = {
  documentSha256: string;
  documentFileName: string;
  existingDocumentId: string | null;
  willCreateDocument: boolean;
  pdfNeeded: boolean;
  counts: AnnotationMergeCounts;
};

export type CommitDocumentImportOptions = {
  createId?: IdFactory;
  importedAt?: string;
  invalidation?: DatabaseInvalidationPublisher;
};

export type DocumentImportCommitResult = {
  documentId: string;
  createdDocument: boolean;
  pdfNeeded: boolean;
  counts: AnnotationMergeCounts;
};

export type ImportDocumentOptions = ParseDocumentImportOptions &
  CommitDocumentImportOptions & {
    confirm?: (
      preview: DocumentImportPreview,
    ) => boolean | Promise<boolean>;
  };

export type ImportDocumentResult =
  | {
      status: "imported";
      preview: DocumentImportPreview;
      result: DocumentImportCommitResult;
    }
  | {
      status: "cancelled";
      preview: DocumentImportPreview;
    };

function randomUuid(): string {
  if (globalThis.crypto?.randomUUID === undefined) {
    throw new Error("This browser does not provide crypto.randomUUID().");
  }

  return globalThis.crypto.randomUUID();
}

export function parseDocumentImportValue(value: unknown): MarginaliaExportV1 {
  return migrateDocumentExportToCurrent(value);
}

export async function parseDocumentImport(
  input: Blob | string | unknown,
  options: ParseDocumentImportOptions = {},
): Promise<MarginaliaExportV1> {
  return parseDocumentImportValue(await readJsonImportValue(input, options));
}

export async function getDocumentImportContext(
  db: MarginaliaDatabase,
  bundle: MarginaliaExportV1,
) {
  const existingDocument = await db.documents
    .where("sha256")
    .equals(bundle.document.sha256)
    .first();
  const documentId = existingDocument?.id ?? PREVIEW_DOCUMENT_ID;
  const localAnnotations = existingDocument
    ? await db.annotations
        .where("documentId")
        .equals(existingDocument.id)
        .toArray()
    : [];
  const incomingIds = bundle.annotations.map((annotation) => annotation.id);
  const collidingAnnotations = await db.annotations.bulkGet(incomingIds);
  const foreignAnnotationIds = new Set(
    collidingAnnotations
      .filter(
        (annotation) =>
          annotation !== undefined && annotation.documentId !== documentId,
      )
      .map((annotation) => annotation!.id),
  );
  const occupiedIds = (await db.annotations.toCollection().primaryKeys()).filter(
    (id): id is string => typeof id === "string",
  );

  return {
    existingDocument,
    documentId,
    localAnnotations,
    foreignAnnotationIds,
    occupiedIds,
  };
}

export async function previewDocumentImport(
  db: MarginaliaDatabase,
  input: MarginaliaExportV1,
  options: Pick<CommitDocumentImportOptions, "createId"> = {},
): Promise<DocumentImportPreview> {
  const bundle = marginaliaExportV1Schema.parse(input);

  return db.transaction(
    "r",
    [db.documents, db.annotations],
    async () => {
      const context = await getDocumentImportContext(db, bundle);
      const merge = mergeAnnotations(
        context.localAnnotations,
        bundle.annotations,
        context.documentId,
        {
          createId: options.createId ?? randomUuid,
          occupiedIds: context.occupiedIds,
          foreignAnnotationIds: context.foreignAnnotationIds,
        },
      );

      return {
        documentSha256: bundle.document.sha256,
        documentFileName: bundle.document.fileName,
        existingDocumentId: context.existingDocument?.id ?? null,
        willCreateDocument: context.existingDocument === undefined,
        pdfNeeded:
          context.existingDocument?.pdfAvailability !== "stored",
        counts: merge.counts,
      };
    },
  );
}

export async function generateUniqueImportDocumentId(
  db: MarginaliaDatabase,
  createId: IdFactory,
): Promise<string> {
  for (let attempt = 0; attempt < 100; attempt += 1) {
    const id = createId();
    if (!isUuid(id)) {
      throw new TypeError("Document ID factory must return a UUID.");
    }
    if ((await db.documents.get(id)) === undefined) {
      return id;
    }
  }

  throw new Error("Could not generate a unique document ID.");
}

export function createImportedDocument(
  bundle: MarginaliaExportV1,
  documentId: string,
  importedAt: string,
): DocumentRecord {
  return {
    schemaVersion: 1,
    id: documentId,
    sha256: bundle.document.sha256,
    pdfFingerprint: bundle.document.pdfFingerprint,
    fileName: bundle.document.fileName,
    title: bundle.document.title,
    author: bundle.document.author,
    byteLength: bundle.document.byteLength,
    pageCount: bundle.document.pageCount,
    pdfAvailability: "external",
    createdAt: importedAt,
    lastOpenedAt: importedAt,
    updatedAt: importedAt,
  };
}

export function createImportedReadingState(
  documentId: string,
  bundle: MarginaliaExportV1,
  importedAt: string,
): ReadingStateRecord | null {
  if (!bundle.readingState) {
    return null;
  }

  return {
    schemaVersion: 1,
    documentId,
    pageIndex: bundle.readingState.pageIndex,
    scrollTop: 0,
    scrollRatio: bundle.readingState.scrollRatio,
    zoom: bundle.readingState.zoom,
    readerCursor: null,
    updatedAt: importedAt,
  };
}

export async function commitDocumentImport(
  db: MarginaliaDatabase,
  input: MarginaliaExportV1,
  options: CommitDocumentImportOptions = {},
): Promise<DocumentImportCommitResult> {
  const bundle = marginaliaExportV1Schema.parse(input);
  const createId = options.createId ?? randomUuid;
  const importedAt = options.importedAt ?? nowIso();
  if (!isIsoUtc(importedAt)) {
    throw new TypeError("Import timestamp must be canonical ISO 8601 UTC.");
  }

  let result: DocumentImportCommitResult;

  try {
    result = await db.transaction(
      "rw",
      [db.documents, db.annotations, db.readingStates],
      async () => {
        const context = await getDocumentImportContext(db, bundle);
        let documentId = context.existingDocument?.id;
        let createdDocument = false;

        if (documentId === undefined) {
          documentId = await generateUniqueImportDocumentId(db, createId);
          await db.documents.add(
            createImportedDocument(bundle, documentId, importedAt),
          );
          createdDocument = true;
        }

        const merge = mergeAnnotations(
          context.localAnnotations,
          bundle.annotations,
          documentId,
          {
            createId,
            occupiedIds: context.occupiedIds,
            foreignAnnotationIds: context.foreignAnnotationIds,
          },
        );

        if (merge.recordsToPut.length > 0) {
          await db.annotations.bulkPut(merge.recordsToPut);
        }

        const readingState = createImportedReadingState(
          documentId,
          bundle,
          importedAt,
        );
        if (readingState) {
          await db.readingStates.put(readingState);
        }

        return {
          documentId,
          createdDocument,
          pdfNeeded:
            context.existingDocument?.pdfAvailability !== "stored",
          counts: merge.counts,
        };
      },
    );
  } catch (error) {
    if (
      error instanceof TypeError ||
      error instanceof ImportValidationError
    ) {
      throw error;
    }
    throw toStorageWriteError(error, "import annotations");
  }

  options.invalidation?.publish({
    type: "document-changed",
    documentId: result.documentId,
  });
  return result;
}

export async function importDocument(
  db: MarginaliaDatabase,
  input: Blob | string | unknown,
  options: ImportDocumentOptions = {},
): Promise<ImportDocumentResult> {
  const bundle = await parseDocumentImport(input, options);
  const preview = await previewDocumentImport(db, bundle, options);

  if (options.confirm && !(await options.confirm(preview))) {
    return { status: "cancelled", preview };
  }

  const result = await commitDocumentImport(db, bundle, options);
  return { status: "imported", preview, result };
}
